import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../../supabase";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../Navbar/Navbar";
import "../Profiles/UserProfile2.css";
import BookingForm from "../Bookings/BookingForm";
import QuickMessageButton from "../Messages/QuickMessageButton";

const categoryLabels = {
  speaking: "Speaking / Preaching",
  music: "Music / Worship",
  teaching: "Teaching / Training",
  pastoral: "Pastoral Care",
  youth: "Youth Ministry",
  children: "Children's Ministry",
  counselling: "Counselling",
  admin: "Administration",
  tech: "Tech / AV",
  events: "Events & Hospitality",
  outreach: "Outreach & Evangelism",
  prayer: "Prayer Ministry",
  mentoring: "Mentoring / Discipleship",
  other: "Other",
};

const getHeroGradient = () =>
  "linear-gradient(135deg, #36454F 0%, #2d3a42 100%)";

// ── Pencil edit button shown on own profile ──────────────────────────────────
const EditBtn = ({ onClick, label = "Edit" }) => (
  <button
    className="inline-edit-btn"
    onClick={onClick}
    aria-label={label}
    title={label}
  >
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  </button>
);

// ── Reusable inline text field editor ────────────────────────────────────────
const InlineTextEditor = ({ value, onChange, onSave, onCancel, multiline = true, placeholder = "" }) => (
  <div className="inline-editor">
    {multiline ? (
      <textarea
        className="inline-editor-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        autoFocus
      />
    ) : (
      <input
        className="inline-editor-input"
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus
      />
    )}
    <div className="inline-editor-actions">
      <button className="inline-save-btn" onClick={onSave}>Save</button>
      <button className="inline-cancel-btn" onClick={onCancel}>Cancel</button>
    </div>
  </div>
);

// ── BookmarkIcon ─────────────────────────────────────────────────────────────
const BookmarkIcon = ({ filled }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
    <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
  </svg>
);

// ── MessageIcon ──────────────────────────────────────────────────────────────
const MessageIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

// ─────────────────────────────────────────────────────────────────────────────

const UserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [activeTab, setActiveTab] = useState("about");
  const [expertiseTags, setExpertiseTags] = useState([]);
  const [inviteCount, setInviteCount] = useState(0);
  const [fulfilledInvites, setFulfilledInvites] = useState([]);
  const [isSaved, setIsSaved] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  // inline editing
  const [editingSection, setEditingSection] = useState(null);
  const [editValues, setEditValues] = useState({});

  // file uploads
  const bannerInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const { userId } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: sessionData, error: userError } =
        await supabase.auth.getSession();
      if (userError || !sessionData?.session) {
        setLoading(false);
        return;
      }

      const currentUserId = sessionData.session.user.id;
      const effectiveUserId = userId || currentUserId;

      setIsOwnProfile(currentUserId === effectiveUserId);

      if (!effectiveUserId) {
        setLoading(false);
        return;
      }

      // Profile data
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*")
        .eq("user_id", effectiveUserId)
        .single();

      if (!error && data) {
        setProfile(data);

        // Expertise tags
        if (data.service_types?.includes("Speaker")) {
          const { data: speakerData } = await supabase
            .from("speaker_expertise")
            .select("expertise_area")
            .eq("user_id", effectiveUserId);
          if (speakerData) setExpertiseTags(speakerData.map((e) => e.expertise_area));
        } else if (data.service_types?.includes("Musician")) {
          const { data: musicianData } = await supabase
            .from("musician_expertise")
            .select("instrument")
            .eq("user_id", effectiveUserId);
          if (musicianData) setExpertiseTags(musicianData.map((e) => e.instrument));
        }
      }

      // Fulfilled invites (accepted bookings where this user is the provider)
      const { data: bookings } = await supabase
        .from("booking_requests")
        .select("id, message, location, start_datetime, sender_id")
        .eq("receiver_id", effectiveUserId)
        .eq("status", "accepted");

      if (bookings && bookings.length > 0) {
        setInviteCount(bookings.length);

        // Fetch sender profiles for church info
        const senderIds = [...new Set(bookings.map((b) => b.sender_id))];
        const { data: senderProfiles } = await supabase
          .from("user_profiles")
          .select("user_id, full_name, church_name, church_city")
          .in("user_id", senderIds);

        const profileMap = {};
        if (senderProfiles) {
          senderProfiles.forEach((p) => { profileMap[p.user_id] = p; });
        }

        const enriched = bookings.map((b) => ({
          ...b,
          sender: profileMap[b.sender_id] || null,
        }));
        setFulfilledInvites(enriched);
      }

      // Is this profile saved by current user?
      if (currentUserId !== effectiveUserId) {
        const { data: savedRow } = await supabase
          .from("saved_profiles")
          .select("id")
          .eq("user_id", currentUserId)
          .eq("saved_user_id", effectiveUserId)
          .maybeSingle();
        setIsSaved(!!savedRow);
      }

      setLoading(false);
    };

    fetchProfile();
  }, [userId]);

  // ── Booking ────────────────────────────────────────────────────────────────
  const handleBook = async () => {
    try {
      const { data: sessionData, error } = await supabase.auth.getSession();
      if (error || !sessionData?.session?.user) {
        alert("Please login to send booking requests");
        return;
      }
      setShowBookingForm(true);
    } catch {
      alert("There was a problem. Please try again.");
    }
  };

  // ── Save / unsave profile ──────────────────────────────────────────────────
  const handleToggleSave = async () => {
    setSavingProfile(true);
    const { data: sessionData } = await supabase.auth.getSession();
    const currentUserId = sessionData?.session?.user?.id;
    const effectiveUserId = userId || currentUserId;

    if (isSaved) {
      await supabase
        .from("saved_profiles")
        .delete()
        .eq("user_id", currentUserId)
        .eq("saved_user_id", effectiveUserId);
      setIsSaved(false);
    } else {
      await supabase
        .from("saved_profiles")
        .insert({ user_id: currentUserId, saved_user_id: effectiveUserId });
      setIsSaved(true);
    }
    setSavingProfile(false);
  };

  // ── Inline edit helpers ────────────────────────────────────────────────────
  const startEdit = (section, initialValues) => {
    setEditingSection(section);
    setEditValues(initialValues);
  };

  const cancelEdit = () => {
    setEditingSection(null);
    setEditValues({});
  };

  const saveEdit = async (fields) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const currentUserId = sessionData?.session?.user?.id;

    const { error } = await supabase
      .from("user_profiles")
      .update(fields)
      .eq("user_id", currentUserId);

    if (!error) {
      setProfile((prev) => ({ ...prev, ...fields }));
    }
    setEditingSection(null);
    setEditValues({});
  };

  // ── Banner upload ──────────────────────────────────────────────────────────
  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingBanner(true);

    const { data: sessionData } = await supabase.auth.getSession();
    const user = sessionData?.session?.user;
    if (!user) { setUploadingBanner(false); return; }

    const fileExt = file.name.split(".").pop();
    const filePath = `profile-pictures/banner-${user.id}-${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("profile-pictures")
      .upload(filePath, file, { cacheControl: "3600", upsert: false });

    if (!uploadError) {
      const { data: { publicUrl } } = supabase.storage
        .from("profile-pictures")
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from("user_profiles")
        .update({ banner_url: publicUrl })
        .eq("user_id", user.id);

      if (updateError) {
        console.error("Failed to save banner URL:", updateError);
        alert("Banner uploaded but could not be saved. Please try again.");
      } else {
        setProfile((prev) => ({ ...prev, banner_url: publicUrl }));
      }
    } else {
      console.error("Banner upload error:", uploadError);
      alert("Failed to upload banner. Please try again.");
    }
    setUploadingBanner(false);
  };

  // ── Profile photo upload ───────────────────────────────────────────────────
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);

    const { data: sessionData } = await supabase.auth.getSession();
    const user = sessionData?.session?.user;
    if (!user) { setUploadingPhoto(false); return; }

    const fileExt = file.name.split(".").pop();
    const filePath = `profile-pictures/${user.id}-${Date.now()}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from("profile-pictures")
      .upload(filePath, file, { cacheControl: "3600", upsert: false });

    if (!uploadError) {
      const { data: { publicUrl } } = supabase.storage
        .from("profile-pictures")
        .getPublicUrl(filePath);

      await supabase
        .from("user_profiles")
        .update({ profile_picture_url: publicUrl })
        .eq("user_id", user.id);

      setProfile((prev) => ({ ...prev, profile_picture_url: publicUrl }));
    }
    setUploadingPhoto(false);
  };

  // ── Pricing display (reused from previous version) ────────────────────────
  const getPricingDisplay = () => {
    if (!profile) return null;
    const ap =
      typeof profile.audience_pricing === "string"
        ? JSON.parse(profile.audience_pricing)
        : profile.audience_pricing || {};

    if (profile.pricing_type === "set_rate" && profile.fixed_price) {
      return (
        <div className="pricing-card">
          <div className="pricing-hero">
            <span className="pricing-currency">£</span>
            <span className="pricing-amount">{profile.fixed_price}</span>
            <span className="pricing-per">/ session</span>
          </div>
          {ap.rate_includes && (
            <div className="pricing-includes">
              <p className="pricing-includes-label">What's included:</p>
              <div className="pricing-include-tags">
                {ap.rate_includes.travelIncluded && <span className="pricing-include-tag">✓ Travel</span>}
                {ap.rate_includes.equipmentIncluded && <span className="pricing-include-tag">✓ Equipment</span>}
                {ap.rate_includes.preparationIncluded && <span className="pricing-include-tag">✓ Preparation</span>}
                {ap.rate_includes.qaIncluded && <span className="pricing-include-tag">✓ Q&A</span>}
              </div>
            </div>
          )}
          {ap.negotiable && <p className="pricing-note">Open to negotiation</p>}
        </div>
      );
    }

    if (ap.honorarium_accepted) {
      return (
        <div className="pricing-card">
          <div className="pricing-flexible-badge">Honorarium accepted</div>
          {ap.suggested_contribution && (
            <div className="pricing-detail-row">
              <span className="pricing-detail-label">Suggested contribution</span>
              <span className="pricing-detail-value">{ap.suggested_contribution}</span>
            </div>
          )}
          {ap.minimum_rate && (
            <div className="pricing-detail-row">
              <span className="pricing-detail-label">Minimum rate</span>
              <span className="pricing-detail-value">{ap.minimum_rate}</span>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="pricing-card">
        <div className="pricing-flexible-badge">Flexible pricing</div>
        <p className="pricing-note">
          Contact {profile.full_name} directly to discuss pricing and arrangements.
        </p>
        {ap.negotiable && <p className="pricing-note">Open to negotiation.</p>}
      </div>
    );
  };

  // ── Availability display ───────────────────────────────────────────────────
  const getAvailabilityDisplay = () => {
    if (!profile) return null;
    const ah =
      typeof profile.available_hours === "string"
        ? JSON.parse(profile.available_hours)
        : profile.available_hours || {};

    return (
      <div className="availability-details">
        <div className="avail-mode-grid">
          <div className={`avail-mode-card ${profile.online_available ? "avail-mode-active" : "avail-mode-inactive"}`}>
            <div className="avail-mode-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </div>
            <p className="avail-mode-label">Online</p>
            <p className="avail-mode-status">{profile.online_available ? "Available" : "Not available"}</p>
          </div>

          <div className={`avail-mode-card ${profile.in_person_available ? "avail-mode-active" : "avail-mode-inactive"}`}>
            <div className="avail-mode-icon">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            </div>
            <p className="avail-mode-label">In Person</p>
            <p className="avail-mode-status">{profile.in_person_available ? "Available" : "Not available"}</p>
          </div>
        </div>


        {ah.min_advance_notice_days > 0 && (
          <div className="availability-item-detail">
            <h4>Advance Notice Required</h4>
            <p>{ah.min_advance_notice_days} day{ah.min_advance_notice_days !== 1 ? "s" : ""} minimum</p>
          </div>
        )}

        {profile.preferred_duration && profile.preferred_duration !== "Flexible" && (
          <div className="availability-item-detail">
            <h4>Preferred Session Duration</h4>
            <p>{profile.preferred_duration}</p>
          </div>
        )}

        {ah.availability_notes && (
          <div className="availability-item-detail">
            <h4>Notes</h4>
            <div className="availability-notes-box">{ah.availability_notes}</div>
          </div>
        )}
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────
  if (loading) return <div className="loading">Loading profile...</div>;
  if (!profile) return <div className="no-profile">No profile found.</div>;

  const hasAvailabilityData = profile.online_available !== null || profile.in_person_available !== null;
  const hasPricingData = profile.pricing_type || profile.audience_pricing;

  return (
    <>
      <Navbar />
      <div className="profile-container">
        <div className="profile-card">

          {/* ── BANNER ──────────────────────────────────────────────────── */}
          <div className="profile-banner-section">
            {profile.banner_url ? (
              <img
                src={profile.banner_url}
                alt="Profile banner"
                className="profile-banner-img"
              />
            ) : (
              <div
                className="profile-banner-gradient"
                style={{ background: getHeroGradient() }}
              />
            )}

            {/* Back button */}
            <button onClick={() => navigate("/explore")} className="back-button">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
              Back
            </button>

            {/* Edit banner (own profile) */}
            {isOwnProfile && (
              <>
                <button
                  className="edit-banner-btn"
                  onClick={() => bannerInputRef.current?.click()}
                  disabled={uploadingBanner}
                >
                  {uploadingBanner ? "Uploading…" : (
                    <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                      Edit banner
                    </>
                  )}
                </button>
                <input
                  ref={bannerInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleBannerUpload}
                />
              </>
            )}

            {/* Avatar — overlapping bottom-left of banner */}
            <div className="profile-avatar-wrapper">
              {profile.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt={profile.full_name}
                  className="profile-avatar"
                />
              ) : (
                <div className="profile-avatar-placeholder">
                  <span>{profile.full_name?.charAt(0) || "U"}</span>
                </div>
              )}
              {/* Edit photo (own profile) */}
              {isOwnProfile && (
                <>
                  <button
                    className="edit-photo-btn"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    title="Change photo"
                  >
                    {uploadingPhoto ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <circle cx="12" cy="12" r="10" />
                      </svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                    )}
                  </button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handlePhotoUpload}
                  />
                </>
              )}
            </div>
          </div>

          {/* ── PROFILE HEADER ──────────────────────────────────────────── */}
          <div className="profile-header">
            {/* Spacer to clear the avatar */}
            <div className="profile-header-avatar-spacer" />

            <div className="profile-header-body">
              {/* Top row: name + action buttons */}
              <div className="profile-header-top">
                <div className="profile-header-identity">
                  {/* Name */}
                  {editingSection === "name" ? (
                    <div>
                      <InlineTextEditor
                        value={editValues.full_name ?? ""}
                        onChange={(v) => setEditValues((p) => ({ ...p, full_name: v }))}
                        onSave={() => saveEdit({ full_name: editValues.full_name })}
                        onCancel={cancelEdit}
                        multiline={false}
                        placeholder="Your name"
                      />
                    </div>
                  ) : (
                    <div className="profile-name-row">
                      <h1 className="profile-name">{profile.full_name}</h1>
                      {isOwnProfile && (
                        <EditBtn
                          onClick={() => startEdit("name", { full_name: profile.full_name })}
                          label="Edit name"
                        />
                      )}
                    </div>
                  )}

                  {/* Tagline */}
                  {profile.bio1 && (
                    <p className="profile-tagline">{profile.bio1}</p>
                  )}

                  {/* Service role tags */}
                  {profile.ways_to_help?.length > 0 && (
                    <div className="service-role-tags">
                      {profile.ways_to_help.map((way) => (
                        <span key={way} className="service-role-tag">
                          {categoryLabels[way] || way}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Meta row: church, location, invite counter */}
                  <div className="profile-meta-row">
                    {profile.church_name && (
                      <span className="profile-meta-item">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 21h18" />
                          <path d="M5 21V7l7-4 7 4v14" />
                          <path d="M9 21v-4h6v4" />
                        </svg>
                        {profile.church_name}
                        {profile.church_city && `, ${profile.church_city}`}
                      </span>
                    )}
                    {profile.location && (
                      <span className="profile-meta-item">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                          <circle cx="12" cy="10" r="3" />
                        </svg>
                        {profile.location}
                      </span>
                    )}
                    {inviteCount > 0 && (
                      <span className="profile-meta-item profile-invite-count">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                        </svg>
                        {inviteCount} fulfilled {inviteCount === 1 ? "invite" : "invites"}
                      </span>
                    )}
                  </div>
                </div>

                {/* Action buttons */}
                <div className="profile-header-actions">
                  {!isOwnProfile && (
                    <button className="btn-invite" onClick={handleBook}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      Invite
                    </button>
                  )}
                  {!isOwnProfile && (
                    <button
                      className={`btn-icon${isSaved ? " btn-icon-active" : ""}`}
                      onClick={handleToggleSave}
                      disabled={savingProfile}
                      title={isSaved ? "Remove from saved" : "Save profile"}
                    >
                      <BookmarkIcon filled={isSaved} />
                    </button>
                  )}
                  {!isOwnProfile && (
                    <QuickMessageButton
                      userId={profile.user_id}
                      userName={profile.full_name}
                      className="btn-icon"
                      buttonText={<MessageIcon />}
                    />
                  )}
                </div>
              </div>

            </div>
          </div>

          {/* ── BODY: two-column ────────────────────────────────────────── */}
          <div className="profile-body">

            {/* ── LEFT SIDEBAR ──────────────────────────────────────────── */}
            <aside className="profile-sidebar">

              {/* Bio card */}
              <div className="sidebar-card">
                <div className="sidebar-card-header">
                  <span className="sidebar-card-title">Bio</span>
                  {isOwnProfile && editingSection !== "bio" && (
                    <EditBtn
                      onClick={() => startEdit("bio", { bio: profile.bio2 || "" })}
                      label="Edit bio"
                    />
                  )}
                </div>
                {editingSection === "bio" ? (
                  <InlineTextEditor
                    value={editValues.bio ?? ""}
                    onChange={(v) => setEditValues((p) => ({ ...p, bio: v }))}
                    onSave={() => saveEdit({ bio2: editValues.bio })}
                    onCancel={cancelEdit}
                    placeholder="Write a short bio…"
                  />
                ) : profile.bio2 ? (
                  <p className="sidebar-bio-text">{profile.bio2}</p>
                ) : (
                  <p className="empty-state-text">{isOwnProfile ? "Add a bio to introduce yourself." : "No bio added yet."}</p>
                )}
              </div>

            </aside>

            {/* ── RIGHT: TABS + CONTENT ──────────────────────────────────── */}
            <div className="profile-main">

              {/* Tab navigation */}
              <div className="section-tabs">
                {[
                  { id: "about", label: "About" },
                  { id: "services", label: "Services" },
                  { id: "invites", label: `Invites${inviteCount > 0 ? ` (${inviteCount})` : ""}` },
                ].map((tab) => (
                  <button
                    key={tab.id}
                    className={`section-tab${activeTab === tab.id ? " section-tab-active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* ── ABOUT TAB ─────────────────────────────────────────── */}
              {activeTab === "about" && (
                <section className="tab-panel">

                  {/* Current Roles */}
                  <div className="content-block">
                    <div className="content-block-header">
                      <h3 className="content-block-title">Current Roles</h3>
                      {isOwnProfile && editingSection !== "roles" && (
                        <EditBtn
                          onClick={() => startEdit("roles", {
                            church_role: profile.church_role || "",
                          })}
                          label="Edit roles"
                        />
                      )}
                    </div>
                    {editingSection === "roles" ? (
                      <div className="inline-editor">
                        <input
                          className="inline-editor-input"
                          type="text"
                          placeholder="Role at church (e.g. Worship Leader)"
                          value={editValues.church_role ?? ""}
                          onChange={(e) => setEditValues((p) => ({ ...p, church_role: e.target.value }))}
                          autoFocus
                        />
                        <div className="inline-editor-actions">
                          <button className="inline-save-btn" onClick={() => saveEdit({
                            church_role: editValues.church_role,
                          })}>Save</button>
                          <button className="inline-cancel-btn" onClick={cancelEdit}>Cancel</button>
                        </div>
                      </div>
                    ) : profile.church_role ? (
                      <div className="roles-list">
                        <div className="role-item">
                          <span className="role-dot" />
                          <span>{profile.church_role}</span>
                          {profile.church_name && (
                            <span className="role-church"> · {profile.church_name}</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="empty-state-text">
                        {isOwnProfile ? "Add your current ministry roles." : "No roles listed."}
                      </p>
                    )}
                  </div>

                  {/* Experience */}
                  <div className="content-block">
                    <div className="content-block-header">
                      <h3 className="content-block-title">Experience</h3>
                      {isOwnProfile && editingSection !== "experience" && (
                        <EditBtn
                          onClick={() => startEdit("experience", { experience: profile.experience || "" })}
                          label="Edit experience"
                        />
                      )}
                    </div>
                    {editingSection === "experience" ? (
                      <InlineTextEditor
                        value={editValues.experience ?? ""}
                        onChange={(v) => setEditValues((p) => ({ ...p, experience: v }))}
                        onSave={() => saveEdit({ experience: editValues.experience })}
                        onCancel={cancelEdit}
                        placeholder="Describe your ministry experience…"
                      />
                    ) : profile.experience ? (
                      <p className="content-text">{profile.experience}</p>
                    ) : (
                      <p className="empty-state-text">
                        {isOwnProfile ? "Share your ministry background and experience." : "No experience listed."}
                      </p>
                    )}
                  </div>

                  {/* Qualifications */}
                  <div className="content-block">
                    <div className="content-block-header">
                      <h3 className="content-block-title">Qualifications</h3>
                      {isOwnProfile && editingSection !== "qualifications" && (
                        <EditBtn
                          onClick={() => startEdit("qualifications", { qualifications: profile.qualifications || "" })}
                          label="Edit qualifications"
                        />
                      )}
                    </div>
                    {editingSection === "qualifications" ? (
                      <InlineTextEditor
                        value={editValues.qualifications ?? ""}
                        onChange={(v) => setEditValues((p) => ({ ...p, qualifications: v }))}
                        onSave={() => saveEdit({ qualifications: editValues.qualifications })}
                        onCancel={cancelEdit}
                        placeholder="List any relevant qualifications or training…"
                      />
                    ) : profile.qualifications ? (
                      <p className="content-text">{profile.qualifications}</p>
                    ) : (
                      <p className="empty-state-text">
                        {isOwnProfile ? "Add relevant qualifications or training." : "No qualifications listed."}
                      </p>
                    )}
                  </div>
                </section>
              )}

              {/* ── SERVICES TAB ──────────────────────────────────────── */}
              {activeTab === "services" && (
                <section className="tab-panel">

                  {/* Expertise / Instruments */}
                  {expertiseTags.length > 0 && (
                    <div className="content-block">
                      <h3 className="content-block-title">
                        {profile.service_types?.includes("Musician") ? "Instruments" : "Areas of Expertise"}
                      </h3>
                      <div className="expertise-tags">
                        {expertiseTags.map((tag) => (
                          <span key={tag} className="expertise-tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Ways to help */}
                  {profile.ways_to_help?.length > 0 && (
                    <div className="content-block">
                      <h3 className="content-block-title">How I Can Serve</h3>
                      <div className="ways-to-help-tags">
                        {profile.ways_to_help.map((way) => (
                          <span key={way} className="way-tag">
                            {categoryLabels[way] || way}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Availability */}
                  {hasAvailabilityData && (
                    <div className="content-block">
                      <h3 className="content-block-title">Availability</h3>
                      {getAvailabilityDisplay()}
                    </div>
                  )}

                  {/* Pricing */}
                  {hasPricingData && (
                    <div className="content-block">
                      <h3 className="content-block-title">Pricing</h3>
                      {getPricingDisplay()}
                    </div>
                  )}

                  {!expertiseTags.length && !profile.ways_to_help?.length && !hasAvailabilityData && !hasPricingData && (
                    <p className="empty-state-text">
                      {isOwnProfile ? "Complete your profile to show your services." : "No service details added yet."}
                    </p>
                  )}
                </section>
              )}

              {/* ── INVITES TAB ───────────────────────────────────────── */}
              {activeTab === "invites" && (
                <section className="tab-panel">
                  {inviteCount > 0 ? (
                    <>
                      <div className="invites-summary">
                        <span className="invites-count">{inviteCount}</span>
                        <span className="invites-label">
                          fulfilled {inviteCount === 1 ? "invite" : "invites"}
                        </span>
                      </div>

                      <div className="invites-list">
                        {fulfilledInvites.map((invite) => {
                          const eventDate = invite.start_datetime
                            ? new Date(invite.start_datetime).toLocaleDateString("en-GB", {
                                month: "short",
                                year: "numeric",
                              })
                            : null;

                          return (
                            <div key={invite.id} className="invite-card">
                              <div className="invite-card-top">
                                {invite.sender?.church_name ? (
                                  <p className="invite-church">{invite.sender.church_name}</p>
                                ) : invite.sender?.full_name ? (
                                  <p className="invite-church">{invite.sender.full_name}</p>
                                ) : (
                                  <p className="invite-church">Church / Organisation</p>
                                )}
                                {eventDate && (
                                  <span className="invite-date">{eventDate}</span>
                                )}
                              </div>
                              {invite.location && (
                                <p className="invite-location">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                    <circle cx="12" cy="10" r="3" />
                                  </svg>
                                  {invite.location}
                                </p>
                              )}
                              {invite.sender?.church_city && (
                                <p className="invite-location">
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 21h18" />
                                    <path d="M5 21V7l7-4 7 4v14" />
                                    <path d="M9 21v-4h6v4" />
                                  </svg>
                                  {invite.sender.church_city}
                                </p>
                              )}
                              {invite.message && (
                                <p className="invite-message">"{invite.message}"</p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <div className="invites-empty">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <p>No fulfilled invites yet.</p>
                    </div>
                  )}
                </section>
              )}
            </div>
          </div>
        </div>

        {/* Booking modal */}
        {showBookingForm && !isOwnProfile && (
          <div
            className="booking-form-overlay"
            onClick={() => setShowBookingForm(false)}
          >
            <div
              className="booking-form-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <BookingForm
                profile={profile}
                onCancel={() => setShowBookingForm(false)}
              />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default UserProfile;
